import React, { useCallback, useRef, useState } from 'react';
import CoreScroll, { CoreScrollElement } from '@nrk/core-scroll/jsx';

import { ScrollArrow } from '../swimlane/swimlaneScroller/ScrollArrow';
import { CoreScrollProps } from '../swimlane/swimlaneScroller/SwimlaneScroller';

import styles from './Scroller.module.css';

export const Scroller = React.forwardRef<
  CoreScroll['el'],
  React.PropsWithChildren & { initialElementIndex?: number; scrollToStartButton?: JSX.Element }
>(({ children, initialElementIndex, scrollToStartButton }, ref) => {
  const arrayChildren = React.Children.toArray(children) as React.ReactElement[];
  const [state, setState] = useState<{ left?: () => void; right?: () => void }>({});
  const scrollToInitialItem = useScrollToInitialItem(initialElementIndex);

  const initScrollArrows = useCallback(
    (target: CoreScrollElement | null) => {
      setState({
        left: target?.scrollLeft ? () => target.scroll('left') : undefined,
        right: target?.scrollRight ? () => target.scroll('right') : undefined,
      });
      if (target) {
        scrollToInitialItem(target);
      }
    },
    [scrollToInitialItem]
  );

  const onScroll: CoreScrollProps['onScrollChange'] = ({ target }) => {
    initScrollArrows(target);
  };

  return (
    <div className={styles.coreScrollContainer} style={{ position: 'relative' }}>
      <ScrollArrow disabled={!state.left} onClick={state.left} withBorder className={styles.navigationArrow} />
      <ScrollArrow right disabled={!state.right} onClick={state.right} withBorder className={styles.navigationArrow} />
      <CoreScroll forwardRef={ref} className={styles.coreScrollElement} items="li" onScrollChange={onScroll}>
        <ul className={styles.list}>
          {arrayChildren.map(child => (
            <li key={'scroller-' + child.key}>{child}</li>
          ))}
          {scrollToStartButton && <li>{scrollToStartButton}</li>}
        </ul>
      </CoreScroll>
    </div>
  );
});

const useScrollToInitialItem = (initialElementIndex?: number) => {
  const setInitialScroll = useRef(true);

  return useCallback(
    (target: CoreScrollElement) => {
      //only scroll to initial element index on first render
      if (setInitialScroll.current === true && initialElementIndex && initialElementIndex >= 0) {
        target?.scroll(target.items[initialElementIndex]);
      }
      setInitialScroll.current = false;
    },
    [initialElementIndex]
  );
};
